/**
 * use-service-charges-admin — OPEN-111 hauler Extra Services admin hook.
 *
 * The admin list includes inactive rows, so the editor can show and reactivate
 * deactivated charges. Mirrors `useProductsAdmin`.
 *
 * OPEN-128 — it reads EVERY page. It used to send one `?includeInactive=true&limit=200`
 * request, so a tenant with more than 200 charges (active + inactive) saw only the first
 * 200 and could not edit the rest. It now walks `keyset=true` pages (id order) and sorts
 * the result into display order. Any failed or unprovable page fails the whole query:
 * the editor shows its error state, never a silently short list.
 *
 * Writes (create / edit / deactivate) go through `authedFetch` directly in
 * ExtraServicesEditor (matching ProductsEditor), invalidating this admin key.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiErrorFromResponse, authedFetch, type WebApiOpts } from '@lodex-pro/web-shared';

/** Which driver screen a charge is entered on (PR #677). DISPOSAL is for charges
 *  only known at or after unloading. */
export type ServiceChargeStage = 'GAUGING' | 'DISPOSAL';

/** A row from GET /v1/service-charges (admin variant — includes inactive rows). */
export interface AdminServiceCharge {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  readonly unitOfMeasure: string;
  readonly defaultRateCents: number;
  readonly sortOrder: number;
  readonly isActive: boolean;
  readonly stage: ServiceChargeStage;
}

export interface ServiceChargesAdminResponse {
  readonly serviceCharges: readonly AdminServiceCharge[];
}

export const SERVICE_CHARGES_ADMIN_QUERY_KEY = ['service-charges', 'catalog', 'admin'] as const;

/** The API's fixed unit enum (service-charges route `UOM_VALUES`) — not free text. */
export const SERVICE_CHARGE_UNITS = ['GAL', 'BBL', 'BAG', 'TON', 'EACH', 'LB', 'HR'] as const;

/** Page size asked for: the endpoint's max. Also an older server's whole-list cap. */
export const SERVICE_CHARGES_PAGE_LIMIT = 200;
/** A walk longer than this is a server bug, not a big tenant (200 x 100 = 20,000 charges). */
export const SERVICE_CHARGES_MAX_PAGES = 100;

const ADMIN_LIST_PATH = '/v1/service-charges?includeInactive=true&keyset=true';

/** sortOrder, label, id: the API's own list order. */
export function compareAdminServiceCharges(a: AdminServiceCharge, b: AdminServiceCharge): number {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  if (a.label !== b.label) return a.label < b.label ? -1 : 1;
  return a.id === b.id ? 0 : a.id < b.id ? -1 : 1;
}

/**
 * Every service charge, active and inactive, in display order. Throws rather than return
 * part of the list: on a non-2xx page, a page with no `serviceCharges` array, a repeated
 * or malformed cursor, a runaway walk, or an older server's (no `nextCursor`) full page.
 */
export async function fetchAllAdminServiceCharges(
  apiOpts: WebApiOpts,
  fetcher: typeof authedFetch = authedFetch,
): Promise<ServiceChargesAdminResponse> {
  const rows: AdminServiceCharge[] = [];
  const seen = new Set<string>();
  let cursor: string | null = null;
  for (let page = 0; page < SERVICE_CHARGES_MAX_PAGES; page += 1) {
    const path: string =
      `${ADMIN_LIST_PATH}&limit=${String(SERVICE_CHARGES_PAGE_LIMIT)}` +
      (cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`);
    const res = await fetcher(apiOpts, path);
    if (!res.ok) throw await apiErrorFromResponse(res, path);
    const body = (await res.json()) as {
      serviceCharges?: unknown;
      nextCursor?: unknown;
    } | null;
    if (body === null || !Array.isArray(body.serviceCharges)) {
      throw new Error(`/v1/service-charges: malformed page ${String(page + 1)}`);
    }
    const list = body.serviceCharges as AdminServiceCharge[];
    rows.push(...list);

    if (!('nextCursor' in body)) {
      // An older server ignored `keyset` and sent one list of at most `limit` rows.
      if (page === 0 && list.length < SERVICE_CHARGES_PAGE_LIMIT) break;
      throw new Error('/v1/service-charges: list may be cut (no nextCursor)');
    }
    const next = body.nextCursor;
    if (next === null) break;
    if (typeof next !== 'string' || next.length === 0 || seen.has(next)) {
      throw new Error('/v1/service-charges: malformed or repeated cursor');
    }
    seen.add(next);
    cursor = next;
    if (page === SERVICE_CHARGES_MAX_PAGES - 1) {
      throw new Error(`/v1/service-charges: more than ${String(SERVICE_CHARGES_MAX_PAGES)} pages`);
    }
  }
  return { serviceCharges: rows.sort(compareAdminServiceCharges) };
}

/** Admin list — includes inactive charges, every page. staleTime 5 min, mirroring
 *  useProductsAdmin for the same near-static reason. */
export function useServiceChargesAdmin(
  apiOpts: WebApiOpts,
): UseQueryResult<ServiceChargesAdminResponse> {
  return useQuery<ServiceChargesAdminResponse>({
    queryKey: SERVICE_CHARGES_ADMIN_QUERY_KEY,
    queryFn: () => fetchAllAdminServiceCharges(apiOpts),
    staleTime: 300_000,
  });
}
